/**
 * Vitrine publique — portage fidèle du design Lovable « Creative Event Hub »
 * (DA « projection privée »), branché sur les vraies API :
 *   · code événement → /api/acces/echanger (vraie session)
 *   · formulaire TVA entreprise → piste `interet_entreprise`
 *   · « faites-nous entrer » → piste `organisateur_souhaite`
 *   · bannière → installation PWA + notifications réelles
 * Les textes passent par t() : FR/NL/EN servis par la base de libellés.
 *
 * L'agenda lit la table `evenement` (module evenements-publics) : à venir
 * autour du visiteur, passés en dessous — la liste de démonstration de 2026-09
 * existera (les cartes pointent vers l'entrée par code).
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Calendar, MapPin, Sparkles, Check, ChevronRight } from 'lucide-react';
import { envoyer } from '../reseau.ts';
import { useSession } from '../etat.tsx';
import { useLibelles } from '../i18n.tsx';
import { Marque, MotMarque } from '../composants/Marque.tsx';
import { BanniereInstallation } from '../composants/BanniereInstallation.tsx';

import heroImage from '../actifs/hero-event.jpg';
import imageDefaut from '../actifs/ev-2.jpg';
import { libelleCategorie, type CategorieEvenement, type EvenementPublic, type ReponseEvenements } from './compte/types.ts';
import organisateurImage from '../actifs/organisateur.jpg';
import ctaHandshake from '../actifs/cta-handshake.jpg';

/* ————————————————— Révélation au scroll ————————————————— */

function Reveal({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          el.classList.add('is-visible');
          io.disconnect();
        }
      },
      { threshold: 0.12 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={`reveal ${className}`}>
      {children}
    </div>
  );
}

/* ————————————————— Mot serif italique dans les titres —————————————————
   Décision Tom (2026-09-02) : chaque mot mis en valeur porte le dégradé
   signature, comme « Redoutable » — l'italique serif ET la couleur. */

function Serif({ children }: { children: ReactNode }) {
  return <em className="text-match-gradient font-serif italic font-medium">{children}</em>;
}

function changerLangue(langue: string) {
  try {
    localStorage.setItem('eventmatch.langue', langue);
  } catch {
    /* stockage indisponible */
  }
  location.reload();
}

/* ————————————————— Page ————————————————— */

export function Vitrine() {
  const { t, langue } = useLibelles();
  const [scrolled, setScrolled] = useState(false);
  const [evenements, setEvenements] = useState<ReponseEvenements | null>(null);

  const chargerEvenements = async (categorie: CategorieEvenement | null = null) => {
    const q = categorie ? `?categorie=${categorie}` : '';
    const r = await fetch(`/api/evenements${q}`).then((x) => (x.ok ? (x.json() as Promise<ReponseEvenements>) : null)).catch(() => null);
    setEvenements(r);
  };
  useEffect(() => {
    void chargerEvenements();
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-void font-sans text-blanc">
      <BanniereInstallation />

      {/* 1. Navigation */}
      <header
        className={`sticky top-0 z-40 w-full transition-colors duration-300 ${
          scrolled ? 'bg-void/95 backdrop-blur-sm' : 'bg-transparent'
        }`}
      >
        <nav className="mx-auto flex max-w-[1200px] items-center justify-between gap-3 px-4 py-3 sm:py-4 sm:px-6">
          <a href="#" className="flex shrink-0 items-center gap-2.5" aria-label="EventMatch.pro">
            <Marque />
            <span className="mot-marque hidden min-[400px]:inline">
              EventMatch<span className="mot-marque__ext">.pro</span>
            </span>
          </a>
          <div className="hidden items-center gap-8 text-[13px] text-fumee md:flex">
            <a href="#agenda" className="transition-colors hover:text-blanc">{t('vitrine.nav.agenda', 'Agenda')}</a>
            <a href="#entreprises" className="transition-colors hover:text-blanc">{t('vitrine.nav.entreprises', 'Entreprises')}</a>
            <a href="#organisateurs" className="transition-colors hover:text-blanc">{t('vitrine.nav.organisateurs', 'Organisateurs')}</a>
            <a href="#premium" className="transition-colors hover:text-blanc">{t('vitrine.nav.premium', 'Premium')}</a>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="label-caps flex items-center gap-1.5 text-fumee sm:gap-2" aria-label="Langue">
              {(['fr', 'nl', 'en'] as const).map((l, i) => (
                <span key={l} className="flex items-center gap-1.5 sm:gap-2">
                  {i > 0 && <span aria-hidden="true">·</span>}
                  <button
                    onClick={() => changerLangue(l)}
                    className={`cursor-pointer uppercase transition-colors hover:text-blanc ${l === langue ? 'text-blanc' : ''}`}
                  >
                    {l}
                  </button>
                </span>
              ))}
            </div>
            {/* « Mon compte » plutôt que « Créer mon compte » : le bouton sert
                aussi celui qui en a déjà un. Il mène à l'espace, qui propose
                la connexion ou la création selon le cas. La création reste
                mise en avant dans le héros, pour le premier visiteur. */}
            <a
              href="/compte"
              className="btn-creme !min-h-0 whitespace-nowrap px-4 py-2 text-[13px] sm:px-5 sm:text-[14px]"
            >
              {t('vitrine.nav.compte', 'Mon compte')}
            </a>
          </div>
        </nav>
      </header>

      {/* 2. Héros */}
      <section className="relative -mt-[60px] flex min-h-[100svh] flex-col justify-end overflow-hidden sm:-mt-[68px]">
        <div className="absolute inset-0">
          <img
            src={evenements?.vedette?.image_url ?? heroImage}
            alt=""
            className="hero-zoom h-full w-full object-cover"
            width={1920}
            height={1280}
            fetchPriority="high"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.15) 100%)',
            }}
          />
        </div>

        <div className="relative mx-auto flex w-full max-w-[1200px] flex-col gap-8 px-4 pb-10 pt-28 sm:gap-10 sm:px-6 sm:pb-16 sm:pt-40 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-[640px]">
            <h1 className="display-title text-[38px] text-blanc sm:text-[56px] lg:text-[76px]">
              {t('vitrine.heros.l1', 'Rejoignez')} <Serif>{t('vitrine.heros.l1_serif', "l'événement")}</Serif>.
              <br />
              {t('vitrine.heros.l2', "L'IA trouve vos")}{' '}
              <span className="text-match-gradient">Perfect Match</span>.
            </h1>
            <p className="mt-6 max-w-[540px] text-[16px] leading-relaxed text-blanc/90 sm:text-[18px]">
              {t(
                'vitrine.heros.texte',
                "Présentez-vous, et l'IA vous trouve des correspondances avec un score de compatibilité, vous propose des plages de rendez-vous aux Meet Corner — et prépare déjà le pitch de l'un et de l'autre sur votre domaine d'intérêt commun.",
              )}
            </p>
          </div>

          {/* Ordre voulu par Tom (2026-09-10) : le code d'abord, l'événement du
              jour juste en dessous, puis la création de compte. */}
          <div className="mx-auto flex w-full max-w-[380px] shrink-0 flex-col gap-4 lg:mx-0">
            <CodeCard />
            <VedetteCard vedette={evenements?.vedette ?? null} charge={evenements !== null} />
            <CarteCreationCompte />
          </div>
        </div>
      </section>

      <AgendaSection donnees={evenements} onChange={chargerEvenements} />
      <EntrepriseSection />
      <PremiumSection />
      <OrganisateurSection />
      <AssistantSection />

      {/* 8. Faites-nous entrer */}
      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-[1200px] px-4 sm:px-6">
          <Reveal>
            <div className="rounded-[10px] bg-charbon p-6 sm:p-10">
              <h2 className="display-title text-[30px] sm:text-[40px]">
                {t('vitrine.souhait.titre_l1', 'Vous participez à un événement')}{' '}
                <Serif>{t('vitrine.souhait.titre_serif', 'prochainement')}</Serif> ?
              </h2>
              <p className="mt-4 max-w-[560px] text-base leading-relaxed text-fumee">
                {t(
                  'vitrine.souhait.texte',
                  "Vous souhaiteriez y voir EventMatch.pro ? Donnez-nous les coordonnées de l'organisateur — nous nous assurons qu'il soit informé de notre existence.",
                )}
              </p>
              <ReferralForm />
            </div>
          </Reveal>
        </div>
      </section>

      <FinalCta />

      {/* 10. Pied de page */}
      <footer className="border-t border-graphite">
        <div className="mx-auto flex max-w-[1200px] flex-col items-center gap-6 px-4 py-10 text-center sm:px-6 lg:flex-row lg:justify-between lg:text-left">
          <a href="#" className="flex items-center gap-2.5" aria-label="EventMatch.pro">
            <Marque />
            <MotMarque />
          </a>
          <p className="max-w-[520px] text-[13px] leading-relaxed text-fumee">
            {t(
              'vitrine.rgpd',
              "Vos données sont hébergées dans l'Union européenne. Consentement explicite pour tout partage, retrait en un clic, droits d'accès et d'effacement exerçables depuis l'application.",
            )}
          </p>
          <p className="text-[13px] text-fumee">
            {t('vitrine.pied.offert', 'Offert par')}{' '}
            <span className="text-match-gradient font-medium">Elite Lead</span>
          </p>
        </div>
      </footer>
    </div>
  );
}

/* ————————————————— Carte code événement (héros) — VRAIE session ————————————————— */

function CodeCard() {
  const { t } = useLibelles();
  const { recharger } = useSession();
  const naviguer = useNavigate();
  const [code, setCode] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!code.trim()) return;
    setEnCours(true);
    setErreur(null);
    const r = await envoyer('/api/acces/echanger', { jeton: code.trim() });
    setEnCours(false);
    if (r.erreur) {
      setErreur(r.erreur);
      return;
    }
    await recharger();
    naviguer('/bienvenue');
  };

  return (
    <div
      id="code"
      className="w-full scroll-mt-24 rounded-[10px] border border-blanc/10 p-5 backdrop-blur-xl sm:p-6"
      style={{
        background: 'rgba(200,200,200,0.08)',
        boxShadow: 'rgba(0,0,0,0.35) 0 10px 30px, inset rgba(255,255,255,0.08) 0 1px 0',
      }}
    >
      <form onSubmit={onSubmit}>
        <p className="label-caps text-blanc">{t('vitrine.code.titre', 'Vous participez à un événement ?')}</p>
        <label htmlFor="event-code" className="label-caps mt-5 block text-fumee">
          {t('vitrine.code.label', 'Entrez le code de votre événement')}
        </label>
        <input
          id="event-code"
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder={t('vitrine.code.exemple', 'ex. k3v9…')}
          className="field-input mt-2"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
        />
        {erreur && (
          <p className="mt-3 text-[13px]" role="alert" style={{ color: 'var(--em-erreur)' }}>
            {erreur}
          </p>
        )}
        <button type="submit" className="btn-creme mt-4 w-full" disabled={enCours}>
          {enCours ? '…' : t('vitrine.code.bouton', 'Entrer')}
        </button>
      </form>
    </div>
  );
}

/* ————————————————— L'événement en vedette (héros) —————————————————
   Celui que la plateforme met en avant, sinon le prochain. Il ouvre sa fiche
   publique — c'est elle qui se référence. */

function VedetteCard({ vedette, charge }: { vedette: EvenementPublic | null; charge: boolean }) {
  const { t, langue } = useLibelles();
  if (!charge) return null;
  if (!vedette) {
    return (
      <a href="#agenda" className="block rounded-[10px] border border-blanc/10 p-4 backdrop-blur-xl transition-colors hover:border-blanc/25" style={{ background: 'rgba(200,200,200,0.06)' }}>
        <p className="label-caps text-fumee">{t('vitrine.jour.titre', 'Événement du jour')}</p>
        <p className="mt-2 text-[14px] leading-relaxed text-blanc/90">{t('vitrine.jour.aucun', 'Aucun événement ouvert aux inscriptions aujourd’hui — parcourez l’agenda.')}</p>
      </a>
    );
  }
  const locale = langue === 'nl' ? 'nl-BE' : langue === 'en' ? 'en-GB' : 'fr-BE';
  const debut = new Date(vedette.debut_le);
  return (
    <a href={`/evenements/${vedette.slug}`} className="block rounded-[10px] border border-blanc/10 p-4 backdrop-blur-xl transition-colors hover:border-blanc/25" style={{ background: 'rgba(200,200,200,0.06)' }}>
      <div className="flex items-center justify-between gap-3">
        <p className="label-caps text-fumee">
          {vedette.aujourdhui ? t('vitrine.jour.titre', 'Événement du jour') : t('vitrine.vedette.badge', 'À la une')}
        </p>
        <span className="bg-match-gradient rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.05em] text-void">
          {vedette.aujourdhui ? t('vitrine.jour.badge', "Aujourd'hui") : libelleCategorie(vedette.categorie, vedette.sous_categorie)}
        </span>
      </div>
      <p className="mt-2 text-[15px] leading-snug text-blanc">{vedette.nom}</p>
      <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-fumee">
        <span className="inline-flex items-center gap-1.5">
          <Calendar size={12} strokeWidth={1.5} />
          {debut.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Brussels' })}
          {' · '}
          {debut.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Brussels' })}
        </span>
        {(vedette.lieu_nom || vedette.lieu_ville) && (
          <span className="inline-flex items-center gap-1.5">
            <MapPin size={12} strokeWidth={1.5} />
            {[vedette.lieu_nom, vedette.lieu_ville].filter(Boolean).join(', ')}
          </span>
        )}
      </p>
      <p className="mt-3 inline-flex items-center gap-1.5 text-[13px] text-blanc">
        {t('vitrine.vedette.decouvrir', 'Découvrir l’événement')} <ArrowRight size={13} strokeWidth={1.5} />
        {vedette.participants > 0 && <span className="text-fumee">· {vedette.participants} {t('evenement.inscrits', 'inscrits')}</span>}
      </p>
    </a>
  );
}

/* ————————————————— Créer mon compte (sous l'événement du jour) ————————————————— */

function CarteCreationCompte() {
  const { t } = useLibelles();
  return (
    <a
      href="/compte/creer"
      className="block rounded-[10px] border border-blanc/10 p-4 backdrop-blur-xl transition-colors hover:border-blanc/25"
      style={{ background: 'rgba(200,200,200,0.06)' }}
    >
      <p className="text-[15px] leading-snug text-blanc">
        {t('vitrine.compte.titre', 'Pas encore de code ?')}{' '}
        <span className="text-match-gradient font-medium">
          {t('vitrine.compte.action', 'Créez votre compte')}
        </span>
      </p>
      <p className="mt-1.5 text-[12px] leading-relaxed text-fumee">
        {t(
          'vitrine.compte.texte',
          'Votre profil vous suit d’un événement à l’autre, et vous rencontrez d’autres entrepreneurs entre deux salons.',
        )}
      </p>
    </a>
  );
}

/* ————————————————— Agenda : données réelles, autour de vous ————————————————— */

const RAYONS: (number | null)[] = [25, 50, 100, 500, null];

function AgendaSection({ donnees, onChange }: { donnees: ReponseEvenements | null; onChange: (categorie: CategorieEvenement | null) => Promise<void> }) {
  const { t } = useLibelles();
  const [categorie, setCategorie] = useState<CategorieEvenement | null>(null);
  const [passesOuverts, setPassesOuverts] = useState(false);
  const [saisieVille, setSaisieVille] = useState<string | null>(null);
  const [erreurZone, setErreurZone] = useState<string | null>(null);

  const changerZone = async (changement: { ville?: string; rayon_km?: number | null }) => {
    setErreurZone(null);
    const r = await fetch('/api/evenements/zone', { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(changement) });
    if (!r.ok) {
      const corps = (await r.json().catch(() => null)) as { erreur?: { message?: string } } | null;
      setErreurZone(corps?.erreur?.message ?? t('vitrine.zone.erreur', 'Ville introuvable'));
      return;
    }
    setSaisieVille(null);
    await onChange(categorie);
  };

  const filtrer = async (c: CategorieEvenement | null) => {
    setCategorie(c);
    await onChange(c);
  };

  const zone = donnees?.zone ?? null;
  const rayonSuivant = zone ? RAYONS[(RAYONS.indexOf(zone.rayon_km) + 1) % RAYONS.length] : 50;

  return (
    <section id="agenda" className="scroll-mt-24 py-16 sm:py-24">
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6">
        <Reveal>
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <span className="badge-glass">{t('vitrine.agenda.badge', 'Agenda · Belgique')}</span>
              <h2 className="display-title mt-4 text-[32px] sm:text-[44px]">
                {t('vitrine.agenda.titre_l1', 'Le mois qui vient,')} <Serif>{t('vitrine.agenda.titre_serif', 'trié')}</Serif> {t('vitrine.agenda.titre_l2', 'pour vous.')}
              </h2>
            </div>
            {zone && (
              <div className="text-[14px] text-fumee">
                <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <MapPin size={14} strokeWidth={1.5} />
                  {t('vitrine.zone.autour', 'Événements autour de vous :')}
                  {saisieVille === null ? (
                    <button onClick={() => setSaisieVille(zone.ville)} className="text-blanc underline decoration-fumee/50 underline-offset-4 hover:decoration-blanc" title={t('vitrine.zone.changer_ville', 'Changer la ville')}>
                      {zone.ville}
                    </button>
                  ) : (
                    <form
                      className="inline-flex items-center gap-2"
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (saisieVille.trim()) void changerZone({ ville: saisieVille.trim() });
                      }}
                    >
                      <input value={saisieVille} onChange={(e) => setSaisieVille(e.target.value)} autoFocus placeholder="Bruxelles, Liège, Gand…" className="field-input !min-h-[34px] !w-44 !py-1 !text-sm" />
                      <button type="submit" className="text-blanc underline">OK</button>
                      <button type="button" onClick={() => setSaisieVille(null)} className="hover:text-blanc">✕</button>
                    </form>
                  )}
                  <button onClick={() => void changerZone({ rayon_km: rayonSuivant })} className="rounded-full border border-graphite px-3 py-1 text-[13px] text-blanc hover:bg-charbon" title={t('vitrine.zone.rayon', 'Changer le rayon')}>
                    {zone.rayon_km === null ? t('vitrine.zone.tous', 'Partout') : `+${zone.rayon_km} km`}
                  </button>
                </p>
                {erreurZone && <p className="mt-1 text-[12px]" style={{ color: 'var(--em-erreur)' }}>{erreurZone}</p>}
              </div>
            )}
          </div>
        </Reveal>

        <div className="mt-8 flex flex-wrap gap-2">
          <button onClick={() => void filtrer(null)} className={`rounded-full border border-graphite px-4 py-2 text-[13px] transition-colors ${categorie === null ? 'bg-blanc text-void' : 'text-fumee hover:text-blanc'}`}>
            {t('vitrine.agenda.tous', 'Tous')}
          </button>
          {(donnees?.categories ?? []).map((c) => (
            <button key={c.cle} onClick={() => void filtrer(c.cle)} className={`rounded-full border border-graphite px-4 py-2 text-[13px] transition-colors ${categorie === c.cle ? 'bg-blanc text-void' : 'text-fumee hover:text-blanc'}`}>
              {c.libelle}
            </button>
          ))}
        </div>

        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {donnees === null && <p className="text-fumee">{t('app.chargement', 'Chargement…')}</p>}
          {donnees?.a_venir.map((ev, i) => <CarteEvenement key={ev.id} ev={ev} index={i} />)}
          {donnees && donnees.a_venir.length === 0 && (
            <p className="max-w-[520px] text-fumee">{t('vitrine.agenda.aucun_a_venir', 'Aucun événement à venir dans cette zone. Élargissez le rayon, ou créez le vôtre.')}</p>
          )}
        </div>
        {donnees && donnees.hors_zone > 0 && (
          <p className="mt-5 text-[13px] text-fumee">
            {t('vitrine.zone.hors_zone', '{n} autre(s) événement(s) hors de ce rayon').replace('{n}', String(donnees.hors_zone))}
            {' — '}
            <button onClick={() => void changerZone({ rayon_km: null })} className="text-blanc underline">{t('vitrine.zone.tous', 'Partout')}</button>
          </p>
        )}

        {donnees && donnees.passes.length > 0 && (
          <div className="mt-12 border-t border-graphite pt-8">
            <button onClick={() => setPassesOuverts(!passesOuverts)} className="flex items-center gap-2 text-[15px] text-fumee hover:text-blanc">
              <ChevronRight size={16} strokeWidth={1.5} className={`transition-transform ${passesOuverts ? 'rotate-90' : ''}`} />
              {t('vitrine.agenda.passes', 'Événements passés')} ({donnees.passes.length})
            </button>
            {passesOuverts && (
              <div className="mt-6 grid gap-5 opacity-80 sm:grid-cols-2 lg:grid-cols-3">
                {donnees.passes.map((ev, i) => <CarteEvenement key={ev.id} ev={ev} index={i} />)}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function CarteEvenement({ ev, index }: { ev: EvenementPublic; index: number }) {
  const { t, langue } = useLibelles();
  const locale = langue === 'nl' ? 'nl-BE' : langue === 'en' ? 'en-GB' : 'fr-BE';
  const debut = new Date(ev.debut_le);
  return (
    <a href={`/evenements/${ev.slug}`} className="block">
      <article className="card-in group overflow-hidden rounded-[14px] bg-charbon transition-transform duration-300 hover:-translate-y-1" style={{ animationDelay: `${index * 45}ms` }}>
        <div className="relative h-[150px] overflow-hidden">
          <img src={ev.image_url ?? imageDefaut} alt={ev.nom} loading="lazy" width={800} height={600} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-charbon via-charbon/20 to-transparent" />
          <div className="absolute left-4 top-4 flex h-[54px] w-[54px] flex-col items-center justify-center rounded-[10px] bg-void/80 backdrop-blur-md">
            <span className="text-[20px] font-medium leading-none text-blanc">{debut.toLocaleDateString(locale, { day: 'numeric', timeZone: 'Europe/Brussels' })}</span>
            <span className="label-caps mt-1 text-[9px] uppercase text-fumee">{debut.toLocaleDateString(locale, { month: 'short', timeZone: 'Europe/Brussels' }).replace('.', '')}</span>
          </div>
          <span className="badge-glass absolute bottom-3 right-3 !py-1 !text-[10px]">{libelleCategorie(ev.categorie, ev.sous_categorie)}</span>
        </div>
        <div className="p-5">
          <h3 className="line-clamp-2 text-base font-medium text-blanc">{ev.nom}</h3>
          <p className="mt-2 flex flex-wrap items-center gap-x-1.5 text-[13px] text-fumee">
            <MapPin size={13} strokeWidth={1.5} /> {[ev.lieu_nom, ev.lieu_ville].filter(Boolean).join(', ')} · {debut.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Brussels' })}
            {ev.distance_km !== null && <span>· {ev.distance_km} km</span>}
          </p>
          <p className="mt-1 text-[12px] text-fumee">{ev.organisation_nom}{ev.participants > 0 ? ` · ${ev.participants} ${t('evenement.inscrits', 'inscrits')}` : ''}</p>
          <p className="mt-4">
            <span className="group/link inline-flex items-center gap-1.5 text-[13px] text-blanc transition-colors hover:text-match-rose">
              {t('vitrine.agenda.decouvrir', 'Découvrir')}
              <ArrowRight size={13} strokeWidth={1.5} className="transition-transform duration-300 group-hover/link:translate-x-0.5" />
            </span>
          </p>
        </div>
      </article>
    </a>
  );
}

/* ————————————————— Entreprises : fond animé + TVA ————————————————— */

function EntrepriseSection() {
  const { t } = useLibelles();
  const points = [
    t('vitrine.entreprise.b1', "Profil d'entreprise pré-rempli via la BCE"),
    t('vitrine.entreprise.b2', 'Salons recommandés selon votre cible'),
    t('vitrine.entreprise.b3', "Perfect Match dès l'inscription"),
    t('vitrine.entreprise.b4', 'Gratuit, sans carte bancaire'),
  ];
  return (
    <section id="entreprises" className="relative scroll-mt-24 overflow-hidden py-20 sm:py-28">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="grid-drift absolute inset-0 opacity-40" />
        <div
          className="aurora absolute -left-32 top-0 h-[420px] w-[420px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(255,61,113,0.35), transparent 70%)' }}
        />
        <div
          className="aurora-slow absolute -right-24 bottom-0 h-[460px] w-[460px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(255,138,61,0.28), transparent 70%)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void via-transparent to-void" />
      </div>

      <div className="relative mx-auto grid max-w-[1200px] gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_460px] lg:items-center">
        <Reveal>
          <span className="badge-glass">{t('vitrine.entreprise.badge', 'Entreprises · création de compte en 40 secondes')}</span>
          <h2 className="display-title mt-6 text-[36px] sm:text-[52px]">
            {t('vitrine.entreprise.titre_l1', 'Votre numéro de TVA suffit.')}{' '}
            <Serif>{t('vitrine.entreprise.titre_serif', 'Le reste')}</Serif>
            {t('vitrine.entreprise.titre_l2', ", l'IA le remplit.")}
          </h2>
          <p className="mt-5 max-w-[560px] text-base leading-relaxed text-fumee">
            {t(
              'vitrine.entreprise.texte',
              'Nous récupérons votre raison sociale, votre secteur, vos implantations et vos réseaux sociaux. Vous validez, vous précisez ce que vous recherchez — et vous recevez déjà les entreprises présentes aux mêmes salons que vous, avec qui connecter avant le jour J.',
            )}
          </p>
          <ul className="mt-8 grid gap-3 sm:grid-cols-2">
            {points.map((p) => (
              <li key={p} className="flex items-start gap-2.5 text-[15px] text-blanc">
                <Check size={16} strokeWidth={1.5} className="mt-0.5 shrink-0 text-match-orange" />
                {p}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal>
          <TvaForm />
        </Reveal>
      </div>
    </section>
  );
}

function TvaForm() {
  const { t, langue } = useLibelles();
  const [tva, setTva] = useState('');
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const r = await envoyer('/api/prospection/pistes', {
      type: 'interet_entreprise',
      demandeur_email: email.trim(),
      message: tva.trim() ? `TVA : BE${tva.trim()}` : undefined,
      langue,
    });
    if (r.erreur) setErreur(r.erreur);
    else setSent(true);
  };

  return (
    <div
      className="border-match rounded-[16px] p-6 backdrop-blur-xl sm:p-8"
      style={{ background: 'rgba(32,32,32,0.75)' }}
    >
      {sent ? (
        <p className="text-base text-blanc" role="status">
          {t('vitrine.capture.merci', 'Merci ! Nous revenons vers vous très vite.')}
        </p>
      ) : (
        <form onSubmit={onSubmit}>
          <p className="label-caps text-fumee">{t('vitrine.entreprise.etape', 'Étape 1 sur 3')}</p>
          <h3 className="display-title mt-3 text-[26px] text-blanc">
            {t('vitrine.entreprise.form_titre', 'Commencez par votre TVA')}
          </h3>
          <label htmlFor="tva" className="label-caps mt-6 block text-fumee">
            {t('vitrine.entreprise.tva', 'Numéro de TVA')}
          </label>
          <div className="mt-2 flex items-center gap-2">
            <span className="flex h-12 items-center rounded-[10px] border border-graphite px-3 text-[15px] text-fumee">
              BE
            </span>
            <input
              id="tva"
              required
              inputMode="numeric"
              value={tva}
              onChange={(e) => setTva(e.target.value)}
              placeholder="0123.456.789"
              className="field-input"
              autoComplete="off"
            />
          </div>
          <label htmlFor="ent-email" className="label-caps mt-5 block text-fumee">
            {t('vitrine.capture.email', 'E-mail professionnel')}
          </label>
          <input
            id="ent-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="prenom@entreprise.be"
            className="field-input mt-2"
          />
          {erreur && (
            <p className="mt-3 text-[13px]" role="alert" style={{ color: 'var(--em-erreur)' }}>
              {erreur}
            </p>
          )}
          <button type="submit" className="btn-creme mt-6 w-full">
            {t('vitrine.entreprise.cta', 'Créer mon compte entreprise')}
            <ChevronRight size={17} strokeWidth={1.5} className="ml-1" />
          </button>
          <p className="mt-4 flex items-center gap-2 text-[12px] text-fumee">
            <Sparkles size={13} strokeWidth={1.5} className="text-match-rose" />
            {t('vitrine.entreprise.note', "Données publiques BCE enrichies par l'IA · hébergement UE")}
          </p>
        </form>
      )}
    </div>
  );
}

/* ————————————————— Premium ————————————————— */

function PremiumSection() {
  const { t } = useLibelles();
  const PREMIUM = [
    { title: t('vitrine.premium.f1_t', 'Matchmaking IA'), text: t('vitrine.premium.f1_x', "L'IA croise les profils et priorise vos rencontres par score de compatibilité.") },
    { title: t('vitrine.premium.f2_t', 'Carte de visite digitale'), text: t('vitrine.premium.f2_x', 'Un profil partageable en un geste, toujours à jour, jamais perdu.') },
    { title: t('vitrine.premium.f3_t', 'Speech IA'), text: t('vitrine.premium.f3_x', 'Le pitch de chaque rendez-vous, préparé sur votre intérêt commun.') },
    { title: t('vitrine.premium.f4_t', 'Prise de rendez-vous'), text: t('vitrine.premium.f4_x', 'Des plages aux Meet Corner proposées et confirmées sans friction.') },
    { title: t('vitrine.premium.f5_t', 'E-mails de suivi'), text: t('vitrine.premium.f5_x', "Pour conclure vos ventes après l'événement, sans y repenser.") },
    { title: t('vitrine.premium.f6_t', 'Historique illimité'), text: t('vitrine.premium.f6_x', 'Chaque contact, chaque échange, retrouvable à tout moment.') },
  ];

  return (
    <section id="premium" className="scroll-mt-24 py-16 sm:py-24">
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6">
        <Reveal>
          <div
            className="border-match relative overflow-hidden rounded-[20px] p-6 sm:p-12"
            style={{
              background:
                'radial-gradient(120% 140% at 0% 0%, rgba(255,61,113,0.22), transparent 55%), radial-gradient(120% 140% at 100% 100%, rgba(255,138,61,0.20), transparent 55%), #0a0a0a',
            }}
          >
            <span className="badge-glass">{t('vitrine.premium.badge', 'Premium · dès 4,99 €/mois')}</span>
            <h2 className="display-title mt-6 text-[34px] sm:text-[52px]">
              {t('vitrine.premium.titre_l1', 'Gratuit pour tous.')}{' '}
              <span className="text-match-gradient">{t('vitrine.premium.titre_grad', 'Redoutable')}</span>{' '}
              {t('vitrine.premium.titre_l2', 'en')} <Serif>premium</Serif>.
            </h2>
            <p className="mt-5 max-w-[600px] text-base leading-relaxed text-blanc/80">
              {t(
                'vitrine.premium.texte',
                'EventMatch.pro est un modèle freemium : gratuit pour les organisateurs et les visiteurs, avec des avantages premium à débloquer dès 4,99 €/mois.',
              )}
            </p>

            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {PREMIUM.map((f) => (
                <div
                  key={f.title}
                  className="group rounded-[14px] border border-blanc/10 bg-void/50 p-6 backdrop-blur-sm transition-transform duration-300 hover:-translate-y-1"
                >
                  <span className="bg-match-gradient inline-flex h-8 w-8 items-center justify-center rounded-full">
                    <Sparkles size={15} strokeWidth={1.5} className="text-blanc" />
                  </span>
                  <h3 className="mt-4 text-base font-medium text-blanc">{f.title}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-fumee">{f.text}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a href="#entreprises" className="btn-creme">{t('vitrine.premium.cta', 'Passer en premium')}</a>
              <span className="text-[13px] text-fumee">{t('vitrine.premium.note', 'Sans engagement · résiliable en un clic')}</span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ————————————————— Organisateurs ————————————————— */

function OrganisateurSection() {
  const { t } = useLibelles();
  const ORGANISATEURS = [
    { title: t('vitrine.orga.f1_t', 'Sponsors sous contrat, sans juriste'), text: t('vitrine.orga.f1_x', "Nous générons une convention type sur base de quelques questions, grâce à l'IA et notre base de connaissance spécialisée.") },
    { title: t('vitrine.orga.f2_t', 'Exposants, stands et partenaires encadrés'), text: t('vitrine.orga.f2_x', 'Acompte et solde, conditions générales, critères de remboursement, calendrier, infos pratiques, contacts — tout au même endroit.') },
    { title: t('vitrine.orga.f3_t', 'Plan des lieux interactif'), text: t('vitrine.orga.f3_x', 'Soumettez la carte du site : les visiteurs cliquent sur chaque stand, obtiennent les contacts et prennent rendez-vous.') },
    { title: t('vitrine.orga.f4_t', 'Billetterie et participants'), text: t('vitrine.orga.f4_x', "Vendez vos tickets, gérez vos inscrits. Le rapport partenaires s'automatise et sert aussi de dossier de presse.") },
    { title: t('vitrine.orga.f5_t', 'Visibilité média'), text: t('vitrine.orga.f5_x', 'Nous référençons les médias susceptibles de relayer votre événement et les plateformes où le publier en quelques clics.') },
  ];

  return (
    <section id="organisateurs" className="scroll-mt-24 py-16 sm:py-24">
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6">
        <Reveal>
          <div className="relative overflow-hidden rounded-[20px]">
            <img
              src={organisateurImage}
              alt=""
              loading="lazy"
              width={1200}
              height={900}
              className="h-[420px] w-full object-cover sm:h-[520px]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-void via-void/70 to-void/10" />
            <div className="absolute inset-x-0 bottom-0 p-6 sm:p-12">
              <span className="badge-glass">{t('vitrine.orga.badge', 'Vous organisez un événement')}</span>
              <h2 className="display-title mt-5 max-w-[760px] text-[36px] text-blanc sm:text-[60px]">
                {t('vitrine.orga.titre_l1', 'Votre salon mérite un')}{' '}
                <Serif>{t('vitrine.orga.titre_serif', 'copilote')}</Serif>.
              </h2>
              <p className="mt-5 max-w-[620px] text-[16px] leading-relaxed text-blanc/85 sm:text-[18px]">
                {t(
                  'vitrine.orga.texte',
                  'Créez votre compte organisateur : billetterie, exposants, sponsors, plan interactif et matchmaking IA pour vos visiteurs. Tout est inclus dès le modèle gratuit.',
                )}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <OrgaCta />
                <span className="text-[13px] text-blanc/70">{t('vitrine.orga.note', 'Gratuit · mise en ligne en 24 h')}</span>
              </div>
            </div>
          </div>
        </Reveal>

        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {ORGANISATEURS.map((o, i) => (
            <Reveal key={o.title}>
              <div className="h-full rounded-[14px] bg-charbon p-6 transition-transform duration-300 hover:-translate-y-1">
                <span className="label-caps text-match-orange">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="mt-3 text-[17px] font-medium text-blanc">{o.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-fumee">{o.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/** CTA organisateur : capture réelle (l'espace s'ouvre sur invitation). */
function OrgaCta() {
  const { t, langue } = useLibelles();
  const [ouvert, setOuvert] = useState(false);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  if (sent) {
    return (
      <p className="text-[15px] text-blanc" role="status">
        {t('vitrine.cta.orga_merci', 'Merci ! Nous ouvrons votre espace organisateur et revenons vers vous.')}
      </p>
    );
  }

  if (!ouvert) {
    return (
      <button className="btn-creme" onClick={() => setOuvert(true)}>
        {t('vitrine.orga.cta', 'Créer mon compte organisateur')}
      </button>
    );
  }

  return (
    <form
      className="flex w-full max-w-[440px] flex-col gap-3 sm:flex-row"
      onSubmit={(e) => {
        e.preventDefault();
        void envoyer('/api/prospection/pistes', {
          type: 'inscription_organisateur',
          demandeur_email: email.trim(),
          langue,
        }).then((r) => {
          if (!r.erreur) setSent(true);
        });
      }}
    >
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t('vitrine.capture.email', 'Votre e-mail professionnel')}
        className="field-input"
        autoFocus
      />
      <button type="submit" className="btn-creme shrink-0">
        {t('vitrine.cta.orga_bouton', 'Ouvrir mon espace')}
      </button>
    </form>
  );
}

/* ————————————————— Assistant : pipeline animé ————————————————— */

function AssistantSection() {
  const { t } = useLibelles();
  const ORG_PREMIUM = [
    { title: t('vitrine.assistant.f1_t', 'Chatbot qui répond pour vous'), text: t('vitrine.assistant.f1_x', 'Il prospecte une partie de vos partenaires et vous accompagne dans votre communication.') },
    { title: t('vitrine.assistant.f2_t', 'Communication en direct'), text: t('vitrine.assistant.f2_x', 'Échangez avec vos inscrits, personnalisez les pages de votre événement.') },
    { title: t('vitrine.assistant.f3_t', 'Automatisations'), text: t('vitrine.assistant.f3_x', "Une série de services se déclenche quand une entreprise cible s'inscrit.") },
  ];

  return (
    <section className="relative overflow-hidden py-16 sm:py-24">
      <div className="pointer-events-none absolute inset-0 opacity-60" aria-hidden="true">
        <div className="grid-drift absolute inset-0" />
        <div className="absolute inset-0 bg-gradient-to-b from-void via-transparent to-void" />
      </div>

      <div className="relative mx-auto max-w-[1200px] px-4 sm:px-6">
        <Reveal>
          <span className="badge-glass">{t('vitrine.assistant.badge', 'Organisateurs · premium')}</span>
          <h2 className="display-title mt-6 text-[34px] sm:text-[48px]">
            {t('vitrine.assistant.titre_l1', 'Un assistant qui ne')}{' '}
            <Serif>{t('vitrine.assistant.titre_serif', 'dort')}</Serif>{' '}
            {t('vitrine.assistant.titre_l2', 'jamais.')}
          </h2>
          <p className="mt-5 max-w-[600px] text-base leading-relaxed text-fumee">
            {t(
              'vitrine.assistant.texte',
              'Votre pipeline de conversion tourne pendant que vous montez les stands. Chaque nœud se déclenche seul, et vous gardez la main à chaque étape.',
            )}
          </p>
        </Reveal>

        <Reveal className="mt-12">
          <PipelineFlow />
        </Reveal>

        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {ORG_PREMIUM.map((f) => (
            <Reveal key={f.title}>
              <div className="h-full rounded-[14px] bg-charbon p-6">
                <h3 className="text-base font-medium text-blanc">{f.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-fumee">{f.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function PipelineFlow() {
  const { t } = useLibelles();
  const PIPELINE = [
    { title: t('vitrine.pipeline.s1_t', 'Inscription détectée'), text: t('vitrine.pipeline.s1_x', "Une entreprise correspondant à votre cible s'inscrit.") },
    { title: t('vitrine.pipeline.s2_t', 'Enrichissement IA'), text: t('vitrine.pipeline.s2_x', "Secteur, taille, signaux d'achat, décideurs identifiés.") },
    { title: t('vitrine.pipeline.s3_t', 'Score de match'), text: t('vitrine.pipeline.s3_x', 'Priorisation automatique des exposants et sponsors.') },
    { title: t('vitrine.pipeline.s4_t', 'Séquence e-mail'), text: t('vitrine.pipeline.s4_x', 'Prise de contact personnalisée, relances programmées.') },
    { title: t('vitrine.pipeline.s5_t', 'Rendez-vous confirmé'), text: t('vitrine.pipeline.s5_x', 'Plage réservée au Meet Corner, pitch préparé.') },
  ];

  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setActive((a) => (a + 1) % PIPELINE.length), 2200);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="rounded-[16px] border border-graphite bg-charbon/60 p-5 backdrop-blur-sm sm:p-8">
      <div className="relative grid gap-4 lg:grid-cols-5">
        <svg
          className="pointer-events-none absolute inset-0 hidden h-full w-full lg:block"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="flow-grad" x1="0" y1="0" x2="1" y2="0">
              <stop stopColor="#FF3D71" />
              <stop offset="1" stopColor="#FF8A3D" />
            </linearGradient>
          </defs>
          <line x1="10%" y1="50%" x2="90%" y2="50%" stroke="url(#flow-grad)" strokeWidth="1.5" className="flow-path" />
        </svg>

        {PIPELINE.map((step, i) => {
          const on = i === active;
          return (
            <div
              key={step.title}
              className={`relative rounded-[12px] border p-5 transition-all duration-500 ${
                on ? 'border-match-rose/60 bg-void/80' : 'border-graphite bg-void/50'
              }`}
            >
              <span
                className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-medium ${
                  on ? 'bg-match-gradient node-pulse text-blanc' : 'border border-graphite text-fumee'
                }`}
              >
                {i + 1}
              </span>
              <h3 className="mt-3 text-[15px] font-medium text-blanc">{step.title}</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-fumee">{step.text}</p>
              {i < PIPELINE.length - 1 && (
                <ArrowRight
                  size={16}
                  strokeWidth={1.5}
                  className={`mt-3 lg:hidden ${on ? 'text-match-orange' : 'text-graphite'}`}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-graphite pt-5 text-[13px] text-fumee">
        <span className="flex items-center gap-2">
          <span className="bg-match-gradient h-2 w-2 rounded-full" /> {t('vitrine.pipeline.actif', 'Flux actif en continu')}
        </span>
        <span>{t('vitrine.pipeline.stat', 'Taux de conversion moyen observé : 3,4× vs relances manuelles')}</span>
      </div>
    </div>
  );
}

/* ————————————————— CTA final ————————————————— */

function FinalCta() {
  const { t } = useLibelles();
  return (
    <section className="relative overflow-hidden py-[100px] sm:py-[140px]">
      <img
        src={ctaHandshake}
        alt=""
        loading="lazy"
        width={1600}
        height={1000}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-void via-void/80 to-void/30" />
      <Reveal className="relative mx-auto max-w-[1200px] px-4 sm:px-6">
        <div className="max-w-[620px]">
          <span className="badge-glass">{t('vitrine.cta.badge', 'Inscription gratuite')}</span>
          <h2 className="display-title mt-6 text-[40px] sm:text-[64px]">
            {t('vitrine.cta.titre_l1', 'La poignée de main')}{' '}
            <Serif>{t('vitrine.cta.titre_serif', 'commence')}</Serif>{' '}
            {t('vitrine.cta.titre_l2', 'ici.')}
          </h2>
          <p className="mt-5 text-[16px] leading-relaxed text-blanc/85 sm:text-[18px]">
            {t(
              'vitrine.cta.texte',
              'Rejoignez les organisateurs et les entreprises qui transforment chaque salon en rendez-vous qualifiés.',
            )}
          </p>
          <div className="mt-10 flex flex-col gap-4 sm:flex-row">
            <a href="#organisateurs" className="btn-creme">{t('vitrine.cta.organisateur', "Organisateur d'événement")}</a>
            <a href="#code" className="btn-ghost">{t('vitrine.cta.participant', 'Participant & networker')}</a>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/* ————————————————— Formulaire « Faites-nous entrer » — VRAIE piste ————————————————— */

function ReferralForm() {
  const { t, langue } = useLibelles();
  const [donnees, setDonnees] = useState({
    demandeur_nom: '',
    evenement_nom: '',
    organisateur_nom: '',
    organisateur_email: '',
    message: '',
  });
  const [sent, setSent] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const poser = (champ: keyof typeof donnees) => (e: { target: { value: string } }) =>
    setDonnees((d) => ({ ...d, [champ]: e.target.value }));

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const r = await envoyer('/api/prospection/pistes', {
      type: 'organisateur_souhaite',
      ...Object.fromEntries(Object.entries(donnees).filter(([, v]) => v.trim() !== '')),
      langue,
    });
    if (r.erreur) setErreur(r.erreur);
    else setSent(true);
  };

  if (sent) {
    return (
      <p className="mt-8 text-base text-blanc" role="status">
        {t('vitrine.souhait.merci', "C'est noté — l'organisateur entendra parler de nous. Merci !")}
      </p>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mt-8 grid gap-5 sm:grid-cols-2">
      <Field
        id="ref-name"
        label={t('vitrine.souhait.votre_nom', 'Votre prénom et nom')}
        placeholder="Marie Dupont"
        value={donnees.demandeur_nom}
        onChange={poser('demandeur_nom')}
        required
      />
      <Field
        id="ref-event"
        label={t('vitrine.souhait.evenement', "Nom de l'événement")}
        placeholder="Salon Entreprendre"
        value={donnees.evenement_nom}
        onChange={poser('evenement_nom')}
      />
      <Field
        id="ref-org"
        label={t('vitrine.souhait.organisateur', "Nom de l'organisateur")}
        placeholder="ASBL Exemple"
        value={donnees.organisateur_nom}
        onChange={poser('organisateur_nom')}
      />
      <Field
        id="ref-email"
        label={t('vitrine.souhait.email_organisateur', "E-mail de l'organisateur")}
        type="email"
        placeholder="contact@evenement.be"
        value={donnees.organisateur_email}
        onChange={poser('organisateur_email')}
        required
      />
      <div className="sm:col-span-2">
        <label htmlFor="ref-context" className="label-caps block text-fumee">
          {t('vitrine.souhait.message', 'Un mot de contexte (facultatif)')}
        </label>
        <textarea
          id="ref-context"
          rows={4}
          value={donnees.message}
          onChange={poser('message')}
          className="field-input mt-2 resize-y"
          placeholder={t('vitrine.souhait.message_ph', 'Pourquoi cet événement mérite EventMatch.pro…')}
        />
      </div>
      {erreur && (
        <p className="text-[13px] sm:col-span-2" role="alert" style={{ color: 'var(--em-erreur)' }}>
          {erreur}
        </p>
      )}
      <div className="sm:col-span-2">
        <button type="submit" className="btn-creme">{t('vitrine.souhait.bouton', "Prévenir l'organisateur")}</button>
      </div>
    </form>
  );
}

function Field({
  id,
  label,
  placeholder,
  type = 'text',
  value,
  onChange,
  required,
}: {
  id: string;
  label: string;
  placeholder: string;
  type?: string;
  value: string;
  onChange: (e: { target: { value: string } }) => void;
  required?: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} className="label-caps block text-fumee">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="field-input mt-2"
      />
    </div>
  );
}
